import * as signalR from '@microsoft/signalr';

class SignalRService {
    constructor() {
        this.connection = null;
        this.isConnected = false;
        this.listeners = {};
        this.activeChats = new Set();
    }

    async connect() {
        if (this.isConnected) {
            console.log('Already connected to SignalR');
            return;
        }

        try {
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(process.env.REACT_APP_HUB_URL || 'http://localhost:5166/chatHub', {
                    accessTokenFactory: () => localStorage.getItem('token'),
                    skipNegotiation: false,
                    transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Information)
                .build();

            this.connection.onreconnecting(() => {
                console.log('SignalR reconnecting...');
                this.isConnected = false;
            });

            this.connection.onreconnected(async () => {
                console.log('SignalR reconnected');
                this.isConnected = true;
                this._registerListeners();
                for (const chatId of this.activeChats) {
                    await this.connection.invoke('JoinChat', chatId);
                }
            });

            this.connection.onclose(() => {
                console.log('SignalR connection closed');
                this.isConnected = false;
            });

            await this.connection.start();
            this.isConnected = true;
            console.log('Connected to SignalR Hub');

            this._registerListeners();
            for (const chatId of this.activeChats) {
                await this.connection.invoke('JoinChat', chatId);
            }
        } catch (error) {
            console.error('Error connecting to SignalR:', error);
            throw error;
        }
    }

    async disconnect() {
        if (this.connection) {
            await this.connection.stop();
            this.isConnected = false;
            this.connection = null;
            console.log('Disconnected from SignalR Hub');
        }
    }

    async joinChat(chatId) {
        this.activeChats.add(chatId);

        if (!this.isConnected) {
            console.warn('Not connected to SignalR, will join on connect');
            return;
        }

        try {
            await this.connection.invoke('JoinChat', chatId);
            console.log(`Joined chat: ${chatId}`);
        } catch (error) {
            console.error('Error joining chat:', error);
        }
    }

    async leaveChat(chatId) {
        if (!this.isConnected) return;

        try {
            await this.connection.invoke('LeaveChat', chatId);
            this.activeChats.delete(chatId);
            console.log(`Left chat: ${chatId}`);
        } catch (error) {
            console.error('Error leaving chat:', error);
        }
    }

    async startTyping(chatId) {
        if (!this.isConnected) return;

        try {
            await this.connection.invoke('StartTyping', chatId);
        } catch (error) {
            console.error('Error sending typing status:', error);
        }
    }

    async stopTyping(chatId) {
        if (!this.isConnected) return;

        try {
            await this.connection.invoke('StopTyping', chatId);
        } catch (error) {
            console.error('Error sending stop typing status:', error);
        }
    }

    on(event, callback) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }

        if (this.listeners[event].includes(callback)) {
            return;
        }

        this.listeners[event].push(callback);

        if (this.isConnected && this.connection) {
            this.connection.on(event, callback);
            console.log(`Registered listener for event: ${event}`);
        }
    }

    off(event, callback) {
        if (this.listeners[event]) {
            this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
        }

        if (this.connection) {
            this.connection.off(event, callback);
            console.log(`Unregistered listener for event: ${event}`);
        }
    }

    _registerListeners() {
        if (!this.connection) return;

        console.log('Registering SignalR listeners:', Object.keys(this.listeners));

        Object.entries(this.listeners).forEach(([event, callbacks]) => {
            callbacks.forEach(callback => {
                this.connection.on(event, callback);
            });
        });
    }
}

const signalRService = new SignalRService();

export default signalRService;
